import type React from "react";
import { useTheme } from "@repo/tokens";
import type {
  PillsAlignItems,
  PillsJustifyContent,
  PillsShape,
  PillsSize,
  PillsVariant,
} from "./types";

export type PillsWebStyles = {
  container: React.CSSProperties;
  labelColor: string;
  iconColor: string;
  dismissIconColor: string;
  iconSize: number;
  dismissIconSize: number;
  textSize: "small" | "medium" | "large";
};

const withHexAlpha = (color: string, alphaHex: string): string => {
  const c = color.trim();
  return c.startsWith("#") && c.length === 7 ? `${c}${alphaHex}` : c;
};

const getDefaultIconSize = (size: PillsSize) =>
  size === "xs" ? 12 : size === "sm" ? 14 : size === "md" ? 16 : 18;

const getDefaultDismissIconSize = (size: PillsSize) =>
  size === "xs" ? 12 : size === "sm" ? 14 : size === "md" ? 16 : 18;

const getTextSize = (size: PillsSize): "small" | "medium" | "large" =>
  size === "lg" ? "large" : size === "md" ? "medium" : "small";

export const usePillsStyles = ({
  variant,
  size,
  color,
  isInteractive,
  isDisabled,
  selected,
  px,
  py,
  gap,
  margin,
  alignItems,
  justifyContent,
  borderRadius,
  borderWidth,
  borderColor: borderColorProp,
  shape,
}: {
  variant: PillsVariant;
  size: PillsSize;
  color: string;
  isInteractive: boolean;
  isDisabled: boolean;
  selected: boolean;
  px?: number;
  py?: number;
  gap?: number;
  margin?: number;
  alignItems: PillsAlignItems;
  justifyContent: PillsJustifyContent;
  borderRadius?: number | string;
  borderWidth?: number;
  borderColor?: string;
  shape: PillsShape;
}): PillsWebStyles => {
  const { theme } = useTheme();

  const paddingX =
    px ??
    (size === "lg"
      ? theme.spacing.md
      : size === "md"
        ? theme.spacing.sm
        : size === "xs"
          ? theme.spacing.xs
          : theme.spacing.sm);

  const paddingY =
    py ??
    (size === "lg"
      ? theme.spacing.sm
      : size === "md"
        ? theme.spacing.xs
        : size === "xs"
          ? 2
          : theme.spacing.xs);

  const baseGap =
    gap ?? (size === "xs" ? 4 : size === "sm" ? 6 : size === "md" ? 8 : 10);

  const schemeMap: Record<string, string> = {
    primary: theme.colors.primary[50],
    secondary: theme.colors.secondary[50],
    info: theme.feedback.info,
    success: theme.colors.success[50],
    warning: theme.colors.warning[50],
    error: theme.colors.error[50],
    neutral: theme.colors.neutral[50],
  };

  const baseColor = color in schemeMap ? schemeMap[color] : color;
  const inverseText = theme.text.inverse;
  const disabledText = theme.text.disabled;

  const radiusFromShape =
    shape === "pill" ? 9999 : shape === "rounded" ? theme.radii.md : 0;

  const finalRadius = borderRadius ?? radiusFromShape;

  let bg = "transparent";
  let borderColor = "transparent";
  let borderStyle: React.CSSProperties["borderStyle"] = "solid";
  let bw = 0;
  let textColor = baseColor;

  if (variant === "solid") {
    bg = baseColor;
    textColor = inverseText;
  } else if (variant === "outline") {
    bw = borderWidth ?? 1;
    borderColor = baseColor;
    textColor = baseColor;
  } else if (variant === "subtle") {
    const scaleKeys = [
      "primary",
      "secondary",
      "success",
      "warning",
      "error",
      "neutral",
    ] as const;
    const scaleKey = scaleKeys.find((k) => k === color);
    if (scaleKey) {
      const scale = theme.colors[scaleKey];
      bg = scale[95];
      textColor = scale[40];
      borderColor = scale[80];
      bw = 1;
    } else if (color === "info") {
      bg = withHexAlpha(baseColor, "1A");
      textColor = baseColor;
      borderColor = withHexAlpha(baseColor, "4D");
      bw = 1;
    } else {
      bg = withHexAlpha(baseColor, "1A");
      textColor = baseColor;
      borderColor = withHexAlpha(baseColor, "40");
      bw = 1;
    }
  } else if (variant === "ghost") {
    textColor = baseColor;
  } else if (variant === "filled") {
    bg = withHexAlpha(baseColor, "2E");
    textColor = baseColor;
  } else if (variant === "sms") {
    bg = theme.surface.card;
    borderColor = theme.colors.primary[70];
    textColor = theme.colors.primary[40];
    bw = 1;
  } else if (variant === "whatsapp") {
    bg = theme.surface.card;
    borderColor = theme.colors.primary[70];
    textColor = theme.colors.success[40];
    bw = 1;
  } else if (variant === "disabled") {
    bg = theme.colors.neutral[90];
    borderColor = theme.colors.neutral[70];
    textColor = theme.colors.neutral[60];
  }

  if (selected) {
    bw = Math.max(bw, borderWidth ?? 2);
    borderColor = baseColor;
  }

  const finalBorderColor = borderColorProp ?? borderColor;
  const finalTextColor = isDisabled ? disabledText : textColor;

  const styles: Record<string, React.CSSProperties> = {
    container: {
      display: "inline-flex",
      flexDirection: "row",
      alignItems,
      justifyContent,
      gap: baseGap,
      paddingLeft: paddingX,
      paddingRight: paddingX,
      paddingTop: paddingY,
      paddingBottom: paddingY,
      margin: margin,
      borderRadius: finalRadius,
      backgroundColor: bg,
      borderColor: finalBorderColor,
      borderWidth: bw,
      borderStyle,
      boxSizing: "border-box",
      cursor: isInteractive && !isDisabled ? "pointer" : "default",
      opacity: isDisabled ? 0.5 : 1,
      transition: isInteractive ? "opacity 120ms ease" : undefined,
      maxWidth: "100%",
    },
  };

  return {
    container: styles.container,
    labelColor: finalTextColor,
    iconColor: finalTextColor,
    dismissIconColor: finalTextColor,
    iconSize: getDefaultIconSize(size),
    dismissIconSize: getDefaultDismissIconSize(size),
    textSize: getTextSize(size),
  };
};
